import React, { useState, useEffect } from "react";

const API_BASE = "https://noisemaps.marble.eu";
// https://noisemaps.marble.eu
// "http://localhost:3001"

const VisitorStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const token = sessionStorage.getItem("jwtToken");
                
                const response = await fetch(`${API_BASE}/api/visitor-stats`, {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "X-User-Role": "admin"
                    }
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch visitor stats");
                }

                const data = await response.json();
                setStats(data);
                setError(null);
            } catch (err) {
                setError(err.message);
                setStats(null);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <div style={styles.container}>
                <h2>Visitor Statistics</h2>
                <p>Loading...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <h2>Visitor Statistics</h2>
                <p style={{ color: "red" }}>Error: {error}</p>
            </div>
        );
    }

    if (!stats) {
        return (
            <div style={styles.container}>
                <h2>Visitor Statistics</h2>
                <p>No data available</p>
            </div>
        );
    }

    const selectedWeekData = selectedWeek
        ? stats.weekSummary.find(w => w.week === selectedWeek)
        : null;

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>👥 Visitor Statistics</h2>

            {/* Summary Cards */}
            <div style={styles.cardsContainer}>
                <div style={styles.card}>
                    <div style={styles.cardValue}>{stats.totalVisitors}</div>
                    <div style={styles.cardLabel}>Total Visitors</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardValue}>{stats.byCountry.length}</div>
                    <div style={styles.cardLabel}>Countries</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardValue}>{stats.weekSummary.length}</div>
                    <div style={styles.cardLabel}>Weeks with Data</div>
                </div>
            </div>

            {/* Top Countries */}
            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🌍 Top Countries</h3>
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.headerRow}>
                                <th style={styles.th}>Country</th>
                                <th style={styles.th}>Visitors</th>
                                <th style={styles.th}>Percentage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.byCountry.slice(0, 10).map((item, idx) => (
                                <tr key={idx} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                                    <td style={styles.td}>{item.country}</td>
                                    <td style={styles.td}>{item.count}</td>
                                    <td style={styles.td}>
                                        {((item.count / stats.totalVisitors) * 100).toFixed(1)}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Weekly Summary */}
            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>📅 Weekly Summary</h3>
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.headerRow}>
                                <th style={styles.th}>Week Start</th>
                                <th style={styles.th}>Total Visitors</th>
                                <th style={styles.th}>Locations</th>
                                <th style={styles.th}>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.weekSummary.map((week, idx) => (
                                <tr key={idx} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                                    <td style={styles.td}>{week.week}</td>
                                    <td style={styles.td}>
                                        <strong>{week.total}</strong>
                                    </td>
                                    <td style={styles.td}>{week.locations.length}</td>
                                    <td style={styles.td}>
                                        <button
                                            style={styles.button}
                                            onClick={() =>
                                                setSelectedWeek(
                                                    selectedWeek === week.week ? null : week.week
                                                )
                                            }
                                        >
                                            {selectedWeek === week.week ? "Hide" : "Show"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Weekly Details */}
            {selectedWeekData && (
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>
                        📍 Details for Week {selectedWeekData.week}
                    </h3>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.headerRow}>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>Visitors</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedWeekData.locations.map((loc, idx) => (
                                    <tr key={idx} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                                        <td style={styles.td}>{loc.country}</td>
                                        <td style={styles.td}>{loc.city}</td>
                                        <td style={styles.td}>{loc.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* All Locations */}
            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🗺️ All Visitor Locations</h3>
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.headerRow}>
                                <th style={styles.th}>Week</th>
                                <th style={styles.th}>Country</th>
                                <th style={styles.th}>City</th>
                                <th style={styles.th}>Visitors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.data.slice(0, 50).map((item, idx) => (
                                <tr key={idx} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                                    <td style={styles.td}>{item.week_start}</td>
                                    <td style={styles.td}>{item.country}</td>
                                    <td style={styles.td}>{item.city}</td>
                                    <td style={styles.td}>{item.visitor_count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {stats.data.length > 50 && (
                    <p style={styles.moreText}>
                        ... and {stats.data.length - 50} more entries
                    </p>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        padding: "20px",
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        margin: "20px",
        fontFamily: "'Montserrat', sans-serif"
    },
    title: {
        fontSize: "28px",
        fontWeight: "bold",
        color: "#333",
        marginBottom: "20px",
        borderBottom: "3px solid #007bff",
        paddingBottom: "10px"
    },
    cardsContainer: {
        display: "flex",
        gap: "20px",
        marginBottom: "30px",
        flexWrap: "wrap",
        justifyContent: "flex-start"
    },
    card: {
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        minWidth: "150px",
        textAlign: "center",
        border: "2px solid #007bff"
    },
    cardValue: {
        fontSize: "32px",
        fontWeight: "bold",
        color: "#007bff",
        marginBottom: "5px"
    },
    cardLabel: {
        fontSize: "14px",
        color: "#666",
        fontWeight: "600"
    },
    section: {
        marginBottom: "30px",
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)"
    },
    sectionTitle: {
        fontSize: "18px",
        fontWeight: "bold",
        color: "#333",
        marginBottom: "15px",
        borderBottom: "2px solid #e0e0e0",
        paddingBottom: "10px"
    },
    tableContainer: {
        overflowX: "auto",
        maxHeight: "400px",
        overflowY: "auto"
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px"
    },
    headerRow: {
        backgroundColor: "#007bff",
        color: "white",
        position: "sticky",
        top: 0
    },
    th: {
        padding: "12px",
        textAlign: "left",
        fontWeight: "600"
    },
    td: {
        padding: "10px 12px",
        borderBottom: "1px solid #e0e0e0"
    },
    rowEven: {
        backgroundColor: "#f9f9f9"
    },
    rowOdd: {
        backgroundColor: "#ffffff"
    },
    button: {
        backgroundColor: "#007bff",
        color: "white",
        border: "none",
        padding: "6px 12px",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "12px",
        fontWeight: "bold",
        transition: "background-color 0.3s"
    },
    moreText: {
        marginTop: "10px",
        fontSize: "12px",
        color: "#666",
        fontStyle: "italic"
    }
};

export default VisitorStats;
